import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { ArrowDownRight, ArrowUpRight, Minus, Plus, Ticket, X } from "lucide-react";
import { ConceptBar } from "../components/ConceptBar";
import { MetricValue } from "../components/MetricValue";
import { SignalCursor } from "../components/SignalCursor";
import { useGsap } from "../hooks/useGsap";
import "../styles/nullfield.css";

type Day = "fri" | "sat" | "sun";
type TicketTier = "night" | "weekend" | "support";

const signalTickerItems = ["NO HEADLINERS", "ONE SHARED SIGNAL", "54 HOURS", "34 LIVE PROJECTS", "12 SPATIAL WORKS"];

const lineup: Record<Day, { time: string; artist: string; format: string }[]> = {
  fri: [
    { time: "20:30", artist: "June Static", format: "live / opening" },
    { time: "22:10", artist: "Oxbow Unit", format: "a/v live" },
    { time: "00:20", artist: "Nera Vale", format: "hybrid set" },
    { time: "02:00", artist: "Soft Crash", format: "dj set" },
  ],
  sat: [
    { time: "19:40", artist: "Kelp", format: "live electronics" },
    { time: "21:30", artist: "Lattice", format: "modular live" },
    { time: "23:50", artist: "Vanta", format: "a/v performance" },
    { time: "02:10", artist: "Palisade", format: "closing set" },
  ],
  sun: [
    { time: "15:00", artist: "Low Current", format: "ambient session" },
    { time: "16:40", artist: "Mora Index", format: "live" },
    { time: "18:20", artist: "North Relay", format: "sound system" },
    { time: "20:00", artist: "Common Ground", format: "all artists" },
  ],
};

const ticketTiers: Record<TicketTier, { label: string; price: number; note: string }> = {
  night: { label: "Одна ночь", price: 2800, note: "Пятница или суббота, вход с 19:00" },
  weekend: { label: "Весь уикенд", price: 6900, note: "Все три дня + дневная программа" },
  support: { label: "Support pass", price: 9800, note: "Уикенд, трансфер и лимитированный принт" },
};

const eventDate = new Date("2026-09-05T20:00:00+03:00").getTime();

function useCountdown() {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const distance = Math.max(0, eventDate - now);
  return {
    days: Math.floor(distance / 86_400_000),
    hours: Math.floor((distance / 3_600_000) % 24),
    minutes: Math.floor((distance / 60_000) % 60),
    seconds: Math.floor((distance / 1000) % 60),
    finished: distance === 0,
  };
}

export function NullFieldPage() {
  const pageRef = useRef<HTMLElement>(null);
  const [day, setDay] = useState<Day>("fri");
  const [tier, setTier] = useState<TicketTier>("weekend");
  const [quantity, setQuantity] = useState(1);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [buyerEmail, setBuyerEmail] = useState("");
  const [orderComplete, setOrderComplete] = useState(false);
  const countdown = useCountdown();
  const total = useMemo(() => ticketTiers[tier].price * quantity, [tier, quantity]);

  useGsap(pageRef, (gsap) => {
    const root = pageRef.current;
    if (!root) return;
    const hero = root.querySelector(".nf-hero__image");
    const title = root.querySelectorAll(".nf-hero__title span");
    const words = root.querySelectorAll(".nf-manifesto__word");
    const artist = root.querySelector(".nf-artist__image");

    gsap.from(title, {
      yPercent: 115,
      duration: 1.15,
      stagger: 0.08,
      ease: "power4.out",
      delay: 0.15,
    });
    gsap.from(".nf-hero__meta > *", {
      opacity: 0,
      y: 24,
      duration: 0.75,
      stagger: 0.08,
      ease: "power3.out",
      delay: 0.6,
    });

    if (hero) {
      gsap.fromTo(
        hero,
        { scale: 1.08 },
        {
          scale: 1,
          ease: "none",
          scrollTrigger: {
            trigger: ".nf-hero",
            start: "top top",
            end: "bottom top",
            scrub: 1,
          },
        },
      );
    }

    words.forEach((word, index) => {
      gsap.fromTo(
        word,
        { color: "rgba(238, 237, 228, .5)" },
        {
          color: "#eeede4",
          scrollTrigger: {
            trigger: ".nf-manifesto",
            start: `top ${72 - index * 2}%`,
            end: `top ${53 - index * 2}%`,
            scrub: true,
          },
        },
      );
    });

    if (artist) {
      gsap.from(artist, {
        scale: 1.12,
        opacity: 0.35,
        scrollTrigger: {
          trigger: ".nf-artist",
          start: "top 82%",
          end: "center 45%",
          scrub: 1,
        },
      });
    }
    gsap.utils.toArray<HTMLElement>(".nf-signal__bars i").forEach((bar, index) => {
      gsap.set(bar, { scaleY: 0.3 + ((index * 13) % 57) / 100 });
      gsap.to(bar, {
        scaleY: () => gsap.utils.random(0.2, 1),
        duration: () => gsap.utils.random(0.16, 0.48),
        delay: (index % 7) * 0.018,
        repeat: -1,
        repeatRefresh: true,
        yoyo: true,
        ease: "sine.inOut",
      });
    });
    gsap.from(".nf-gallery figure", {
      clipPath: "inset(12% 12% 12% 12%)",
      scale: 0.95,
      opacity: 0.4,
      duration: 1,
      stagger: 0.08,
      ease: "power4.out",
      scrollTrigger: { trigger: ".nf-gallery", start: "top 72%" },
    });
    gsap.to(".nf-gallery figure:nth-child(2) img", {
      yPercent: 8,
      ease: "none",
      scrollTrigger: {
        trigger: ".nf-gallery",
        start: "top bottom",
        end: "bottom top",
        scrub: 1,
      },
    });
  });

  function submitOrder(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!buyerEmail.trim()) return;
    setOrderComplete(true);
  }

  return (
    <main id="main" className="nf-page" ref={pageRef}>
      <ConceptBar label="Фестиваль новой электроники" dark />
      <SignalCursor className="nf-signal-cursor" />

      <nav className="nf-nav" aria-label="Навигация NULL/FIELD">
        <a className="nf-logo" href="#top" aria-label="NULL/FIELD — наверх">
          NULL<span>/</span>FIELD
        </a>
        <div className="nf-nav__links">
          <a href="#lineup">Лайнап</a>
          <a href="#place">Место</a>
          <a href="#tickets">Билеты</a>
        </div>
        <a className="nf-nav__ticket" href="#tickets" data-magnetic>
          Билет <ArrowUpRight size={16} />
        </a>
      </nav>

      <header className="nf-hero" id="top">
        <img
          className="nf-hero__image"
          src="/assets/nullfield/crowd.webp"
          alt="Аудиовизуальный перформанс в индустриальном зале фестиваля"
          width="1800"
          height="1200"
        />
        <div className="nf-hero__shade" />
        <div className="nf-hero__meta">
          <p>05—07 / 09 / 2026</p>
          <p>Цех 14, береговой кластер</p>
          <p>54°42′ N · 20°30′ E</p>
        </div>
        <h1 className="nf-hero__title" aria-label="Null Field">
          <span>NULL</span>
          <span>/FIELD</span>
        </h1>
        <div className="nf-hero__bottom">
          <p>
            Три дня музыки, света и пространственных практик в бывшем судоремонтном
            цехе
          </p>
          <a href="#manifesto" aria-label="Читать манифест">
            <ArrowDownRight />
          </a>
        </div>
      </header>

      <div className="nf-marquee" aria-hidden="true">
        <div className="nf-marquee__track">
          {[0, 1].map((group) => (
            <div className="nf-marquee__group" key={group}>
              {[0, 1].flatMap((cycle) => signalTickerItems.map((item) => (
                <span key={`${group}-${cycle}-${item}`}>{item}<i>●</i></span>
              )))}
            </div>
          ))}
        </div>
      </div>

      <section className="nf-manifesto" id="manifesto">
        <p className="nf-kicker">Манифест / 01</p>
        <h2>
          {"Мы выключаем привычный город, чтобы услышать новый ритм пространства."
            .split(" ")
            .map((word, index) => (
              <span className="nf-manifesto__word" key={`${word}-${index}`}>
                {word}{" "}
              </span>
            ))}
        </h2>
        <div className="nf-manifesto__aside">
          <p>
            Здесь нет VIP-зон и главной сцены. Звук, свет, архитектура и зритель
            работают как одна система.
          </p>
          <p>48 часов звука, света и пространственных работ на берегу Балтики.</p>
        </div>
      </section>

      <section className="nf-signal" aria-labelledby="nf-signal-title">
        <div className="nf-signal__head">
          <p className="nf-kicker">Общий сигнал / live</p>
          <h2 id="nf-signal-title" data-split>54 часа без паузы между музыкой, светом и архитектурой.</h2>
        </div>
        <div className="nf-signal__bars" aria-label="Динамическая визуализация сигнала">
          {Array.from({ length: 54 }).map((_, index) => <i key={index} />)}
        </div>
        <div className="nf-signal__metrics">
          <article><strong><MetricValue value={34} /></strong><span>live-проекта</span></article>
          <article><strong><MetricValue value={12} /></strong><span>пространственных работ</span></article>
          <article><strong><MetricValue value={4} /></strong><span>звуковые системы</span></article>
          <article><strong><MetricValue value={0} /></strong><span>VIP-зон</span></article>
        </div>
      </section>

      <section className="nf-lineup" id="lineup">
        <div className="nf-section-head">
          <p className="nf-kicker">Программа / 02</p>
          <h2 data-split>Лайнап без иерархии</h2>
        </div>

        <div className="nf-lineup__days" role="tablist" aria-label="Дни фестиваля">
          {(["fri", "sat", "sun"] as Day[]).map((item, index) => (
            <button
              type="button"
              role="tab"
              aria-selected={day === item}
              className={day === item ? "is-active" : ""}
              onClick={() => setDay(item)}
              key={item}
            >
              <span>0{index + 1}</span>
              {item === "fri" ? "Пт · 05" : item === "sat" ? "Сб · 06" : "Вс · 07"}
            </button>
          ))}
        </div>

        <div className="nf-lineup__grid">
          <div className="nf-lineup__list" role="tabpanel">
            {lineup[day].map((slot) => (
              <article key={slot.artist} data-spotlight>
                <time>{slot.time}</time>
                <h3>{slot.artist}</h3>
                <p>{slot.format}</p>
                <ArrowUpRight aria-hidden="true" />
              </article>
            ))}
          </div>
          <figure className="nf-artist">
            <img
              className="nf-artist__image"
              src="/assets/nullfield/performance.webp"
              alt="Музыкант фестиваля работает с модульным синтезатором"
              width="1200"
              height="1800"
              loading="lazy"
            />
            <figcaption>
              <span>Artist focus</span>
              <strong>{lineup[day][1].artist}</strong>
            </figcaption>
          </figure>
        </div>
      </section>

      <section className="nf-gallery" aria-label="Пространства фестиваля">
        <figure>
          <img src="/assets/nullfield/crowd.webp" alt="Главный машинный зал во время ночной программы" width="1800" height="1200" loading="lazy" />
          <figcaption><span>Machine hall</span><b>7 600 capacity / 42-channel sound</b></figcaption>
        </figure>
        <figure>
          <img src="/assets/nullfield/performance.webp" alt="Артистка работает с модульным синтезатором" width="1200" height="1800" loading="lazy" />
          <figcaption><span>Signal room</span><b>Live electronics / 360° audience</b></figcaption>
        </figure>
        <figure>
          <img src="/assets/nullfield/hero.webp" alt="Световая инсталляция в индустриальном пространстве" width="1536" height="1024" loading="lazy" />
          <figcaption><span>Dry dock</span><b>Spatial light / Baltic horizon</b></figcaption>
        </figure>
      </section>

      <section className="nf-place" id="place">
        <div className="nf-place__intro">
          <p className="nf-kicker">Пространство / 03</p>
          <h2 data-split>12 000 м²<br />сырого звука</h2>
        </div>
        <div className="nf-place__cards">
          <article data-spotlight>
            <span>01</span><strong>7 600</strong><small>вместимость</small><h3>Machine hall</h3>
            <p>Пространственный звук, лазерная сетка и живые A/V-сеты.</p>
          </article>
          <article data-spotlight>
            <span>02</span><strong>42</strong><small>канала звука</small><h3>Dry dock</h3>
            <p>Низкочастотная сцена под открытым небом и вид на воду.</p>
          </article>
          <article data-spotlight>
            <span>03</span><strong>360°</strong><small>вокруг артиста</small><h3>Signal room</h3>
            <p>Дневные лекции, лаборатория синтеза и тихие перформансы.</p>
          </article>
        </div>
        <p className="nf-place__address">
          Россия, Балтийский берег, Промышленный проезд, 14.
        </p>
      </section>

      <section className="nf-tickets" id="tickets">
        <div className="nf-tickets__headline">
          <p className="nf-kicker">Доступ / 04</p>
          <h2 data-split>Войти в поле</h2>
          <div className="nf-countdown" aria-label="До начала фестиваля">
            {countdown.finished ? (
              <strong>Событие завершилось</strong>
            ) : (
              <>
                <div><strong>{countdown.days}</strong><span>дней</span></div>
                <div><strong>{String(countdown.hours).padStart(2, "0")}</strong><span>часов</span></div>
                <div><strong>{String(countdown.minutes).padStart(2, "0")}</strong><span>минут</span></div>
                <div><strong>{String(countdown.seconds).padStart(2, "0")}</strong><span>секунд</span></div>
              </>
            )}
          </div>
        </div>

        <div className="nf-ticket-builder">
          <div className="nf-ticket-builder__tiers">
            {(Object.keys(ticketTiers) as TicketTier[]).map((item) => (
              <button
                type="button"
                key={item}
                className={tier === item ? "is-active" : ""}
                onClick={() => setTier(item)}
              >
                <span>{ticketTiers[item].label}</span>
                <strong>{ticketTiers[item].price.toLocaleString("ru-RU")} ₽</strong>
                <small>{ticketTiers[item].note}</small>
              </button>
            ))}
          </div>
          <div className="nf-ticket-builder__summary">
            <Ticket size={34} strokeWidth={1.5} />
            <p>Вы выбрали</p>
            <h3>{ticketTiers[tier].label}</h3>
            <div className="nf-quantity" aria-label="Количество билетов">
              <button
                type="button"
                onClick={() => setQuantity((value) => Math.max(1, value - 1))}
                aria-label="Уменьшить количество"
              >
                <Minus />
              </button>
              <strong>{quantity}</strong>
              <button
                type="button"
                onClick={() => setQuantity((value) => Math.min(6, value + 1))}
                aria-label="Увеличить количество"
              >
                <Plus />
              </button>
            </div>
            <div className="nf-ticket-builder__total">
              <span>Итого</span>
              <strong>{total.toLocaleString("ru-RU")} ₽</strong>
            </div>
            <button className="nf-primary" type="button" onClick={() => setCheckoutOpen(true)} data-magnetic>
              Продолжить <ArrowUpRight />
            </button>
            <small>Билет можно вернуть до 20 мая включительно.</small>
          </div>
        </div>
      </section>

      <footer className="nf-footer">
        <div className="nf-logo">NULL<span>/</span>FIELD</div>
        <p>06—07 June / Baltic coast</p>
        <a href="#top">Наверх ↑</a>
      </footer>

      {checkoutOpen && (
        <div className="nf-modal" role="dialog" aria-modal="true" aria-labelledby="nf-checkout-title">
          <button
            className="nf-modal__backdrop"
            type="button"
            aria-label="Закрыть окно"
            onClick={() => setCheckoutOpen(false)}
          />
          <div className="nf-modal__card">
            <button
              className="nf-modal__close"
              type="button"
              onClick={() => setCheckoutOpen(false)}
              aria-label="Закрыть"
            >
              <X />
            </button>
            {orderComplete ? (
              <div className="nf-modal__success" role="status" aria-live="polite">
                <span>Signal received</span>
                <h2>Место зарезервировано</h2>
                <p>
                  Билет и маршрут до площадки отправлены на {buyerEmail}.
                </p>
                <button
                  className="nf-primary"
                  type="button"
                  onClick={() => {
                    setCheckoutOpen(false);
                    setOrderComplete(false);
                    setBuyerEmail("");
                  }}
                >
                  Закрыть
                </button>
              </div>
            ) : (
              <form onSubmit={submitOrder}>
                <span>Последний шаг</span>
                <h2 id="nf-checkout-title">Куда отправить билет?</h2>
                <label>
                  Email
                  <input
                    type="email"
                    required
                    value={buyerEmail}
                    onChange={(event) => setBuyerEmail(event.target.value)}
                    placeholder="you@example.com"
                  />
                </label>
                <div>
                  <p>{quantity} × {ticketTiers[tier].label}</p>
                  <strong>{total.toLocaleString("ru-RU")} ₽</strong>
                </div>
                <button className="nf-primary" type="submit">
                  Зарезервировать <ArrowUpRight />
                </button>
                <small>Бронь удерживается 15 минут. После подтверждения билет придёт на email.</small>
              </form>
            )}
          </div>
        </div>
      )}
    </main>
  );
}
